/**
 * Where the IVORI Edu app lives on a given page load.
 *
 * Products are retired from sub-domains and served under a path prefix on
 * the main domain: IVORI Edu is https://ivoriai.com/edu/... (a future
 * product gets its own prefix, e.g. /csuite). One SPA build serves all of it.
 *
 * React Router gets basename "/edu" when the page loaded on a path-prefix
 * host AND the path is exactly "/edu" or starts with "/edu/". Everywhere
 * else the basename is "" (pathbuddy.com, the old edu hosts, exam hosts,
 * csuite.localhost, edu.localhost, preview hosts), which is how the app
 * behaved before the move. The basename is computed once, when the router
 * mounts: the company home and the edu app are different page loads, joined
 * by plain <a href>, never by <Link>.
 *
 * <Link to>, navigate() and <Navigate> pick the prefix up from the router.
 * Raw navigations (window.location, plain <a href>, origin + path strings,
 * history.replaceState) do not, so they go through withAppBasename /
 * appUrl below. useLocation().pathname stays router-relative ("/chat"), so
 * path checks written against it keep working; use stripAppBasename when
 * reading window.location.pathname.
 *
 * This module has no imports on purpose: index.html mirrors the segment
 * match in ES5 and the tests compare them.
 */

export const EDU_BASENAME = '/edu';
export const MAIN_ORIGIN = 'https://ivoriai.com';
export const EDU_CANONICAL_BASE = 'https://ivoriai.com/edu';
/** The Edu home is the site root; every other Edu page stays under /edu. */
export const EDU_HOME_URL = 'https://ivoriai.com/';

/**
 * Hosts where products live under a path prefix. Exact match only (house
 * style: a routing decision must never swallow an unrelated host, so
 * "evilivoriai.com" and "ivoriai.com.evil.com" do not match).
 * localhost and 127.0.0.1 let the edu app run at localhost:3000/edu;
 * ivori.localhost mirrors production.
 */
export const PATH_PREFIX_HOSTS = [
  'ivoriai.com',
  'www.ivoriai.com',
  'ivori.ai',
  'www.ivori.ai',
  'ivori.localhost',
  'localhost',
  '127.0.0.1',
];

/**
 * Hosts whose root "/" is the Edu home: the page at ivoriai.com/ is the page
 * that used to be ivoriai.com/edu, while every other Edu page keeps its /edu
 * path (ivoriai.com/edu/lnat). The router still runs under the /edu basename
 * there; rootAliasHistory (app/) maps browser "/" to router "/edu" and back.
 * ivoriai.com/edu itself is redirected to "/" (public/index.html host map).
 * localhost keeps the plain dev layout (Edu at localhost:3000/edu).
 */
export const EDU_ROOT_HOSTS = [
  'ivoriai.com',
  'www.ivoriai.com',
  'ivori.localhost',
];

function currentLocation() {
  if (typeof window === 'undefined' || !window.location) return {};
  return window.location;
}

/** True for "/edu" and "/edu/..." (any case); false for "/education", "/edux". */
export function isEduPrefixedPath(pathname) {
  if (typeof pathname !== 'string') return false;
  const p = pathname.toLowerCase();
  if (p.indexOf(EDU_BASENAME) !== 0) return false;
  return p.length === EDU_BASENAME.length || p.charAt(EDU_BASENAME.length) === '/';
}

/** Exact-match test against PATH_PREFIX_HOSTS. */
export function servesProductsUnderPath(hostname) {
  const h = (hostname || '').toLowerCase();
  return PATH_PREFIX_HOSTS.indexOf(h) !== -1;
}

/** Exact-match test against EDU_ROOT_HOSTS. */
export function servesEduAtRoot(hostname) {
  const h = (hostname || '').toLowerCase();
  return EDU_ROOT_HOSTS.indexOf(h) !== -1;
}

/** Router basename for a host and path: "/edu" or "". */
export function resolveRouterBasename(hostname, pathname) {
  if (!servesProductsUnderPath(hostname)) return '';
  if (isEduPrefixedPath(pathname)) return EDU_BASENAME;
  return servesEduAtRoot(hostname) && (pathname === '/' || pathname === '') ? EDU_BASENAME : '';
}

/** Router basename for the page that is loaded right now. */
export function getAppBasename() {
  const loc = currentLocation();
  return resolveRouterBasename(loc.hostname, loc.pathname);
}

function isRootRelative(path) {
  return typeof path === 'string' && path.charAt(0) === '/' && path.charAt(1) !== '/';
}

/**
 * "/chat" -> "/edu/chat" when the app runs under /edu, otherwise unchanged.
 * "/" -> "/edu" ("/" where the Edu home is the root, EDU_ROOT_HOSTS);
 * "/a?b#c" -> "/edu/a?b#c". Anything that is not root-relative
 * ("#x", "https://...", "//evil.com", "", null) and any path already under
 * /edu is returned unchanged.
 */
export function withAppBasename(path) {
  if (!isRootRelative(path)) return path;
  const basename = getAppBasename();
  if (!basename || isEduPrefixedPath(path.split(/[?#]/)[0])) return path;
  const second = path.charAt(1);
  const isHome = path === '/' || second === '?' || second === '#';
  if (isHome && servesEduAtRoot(currentLocation().hostname)) return path;
  if (path === '/') return basename;
  if (second === '?' || second === '#') return `${basename}${path.slice(1)}`;
  return `${basename}${path}`;
}

/**
 * The path of the page that is loaded, prefix included ("/edu/lnat"), for
 * links that must point back at this page ("#section" resolves against
 * <base href="/">, so an in-page link needs the full path in front).
 */
export function getPagePath() {
  const p = currentLocation().pathname;
  return typeof p === 'string' && p ? p : '/';
}

/** "/edu/lnat" -> "/lnat", "/edu" -> "/"; anything else unchanged. */
export function stripAppBasename(pathname) {
  if (typeof pathname !== 'string') return pathname;
  if (!isEduPrefixedPath(pathname)) return pathname;
  const rest = pathname.slice(EDU_BASENAME.length);
  return rest || '/';
}

/** Absolute URL of an app path on this origin, including the prefix. */
export function appUrl(path) {
  const loc = currentLocation();
  const origin = loc.origin || '';
  return `${origin}${withAppBasename(path)}`;
}
